import React, { useCallback, useEffect, useState } from 'react'
import {
    chain, forEach, maxBy, sortBy, uniqBy, isNil
} from 'lodash'
import { NotFound } from 'components'
import FullPageLoading from 'components/FullPageLoading'
import { useEventSlug, useQuery } from 'hooks'
import { NavigationMenusQuery, NavigationMenusQueryVariables } from 'generated/graphql'
import { updateNavigationItem, updateNavigationMenu } from '../graphql/mutation'
import { navigationMenusQuery } from '../graphql/query'
import TopNav from './TopNav'

export type NavigationMenu = NavigationMenusQuery['event']['navigationMenus'][0];
export type NavigationItem = NavigationMenusQuery['event']['navigationItems'][0];

type OrderItem = {
    id: number
    order: number
    previousOrder?: number
}

export const reorderItems = (items: OrderItem[] = [], indexFrom: number, indexTo: number) => {
    // no order changed, let's skip
    if (indexFrom === indexTo || isNil(indexFrom) || isNil(indexTo)) {
        return
    }

    // The goal is to update the new order of the moving item only.
    // 1. Calculate the new order by getting the average of orders from the sibling items
    // 2. If the new order is duplicated (probably because the average is integer), refresh the order of all items

    const sortedItems = sortBy(items, 'order')
    const movingItem = sortedItems[indexFrom]

    // Remove moving item from the list to get the new sibling item order
    sortedItems.splice(indexFrom, 1)
    const prevItemOrder = sortedItems[indexTo - 1]?.order
    const nextItemOrder = sortedItems[indexTo]?.order
    const shiftingOrderStep = 1000
    // Insert again to the list at the new index
    sortedItems.splice(indexTo, 0, movingItem)

    movingItem.previousOrder = movingItem.order
    if (indexTo === 0) {
        movingItem.order = nextItemOrder - shiftingOrderStep
    } else if (indexTo === sortedItems.length - 1) {
        movingItem.order = prevItemOrder + shiftingOrderStep
    } else {
        movingItem.order = Math.floor((prevItemOrder + nextItemOrder) / 2)
    }

    if (uniqBy(sortedItems, ({ order }) => order).length !== sortedItems.length) {
        forEach(sortedItems, (item, index) => {
            item.previousOrder = item.previousOrder || item.order
            item.order = index * shiftingOrderStep
        })
    }
}

export const updateOrderItems = (items: OrderItem[], syncFunc: ({ order: number }, id) => void) => {
    chain(items)
        // Filter modified items
        .filter((item) => !isNil(item.previousOrder) && item.previousOrder !== item.order)
        // Execute API to sync items
        .forEach((item) => {
            syncFunc({ order: item.order }, item.id)
            delete item.previousOrder
        })
        .value()
}

export const findMaxOrder = (items: OrderItem[] = []) => {
    const maxOrderItem = maxBy(items || [], (i) => i.order)
    return maxOrderItem ? maxOrderItem.order : 0
}

const NavigationEditor = () => {
    const eventSlug = useEventSlug()
    const [navigationMenus, setNavigationMenus] = useState<NavigationMenu[]>()
    const [navigationItems, setNavigationItems] = useState<NavigationItem[]>()
    const { data, requestStatus, fetchData } = useQuery<NavigationMenusQuery, NavigationMenusQueryVariables>(
        navigationMenusQuery, { eventId: eventSlug }
    )

    useEffect(() => {
        if (data) {
            setNavigationMenus(data.event.navigationMenus)
            setNavigationItems(data.event.navigationItems)
        }
    }, [data])

    const handleDraggedMenuItem = useCallback((indexFrom, indexTo) => {
        reorderItems([...navigationMenus, ...navigationItems], indexFrom, indexTo)

        // sync new order with server
        updateOrderItems(navigationMenus, updateNavigationMenu)
        updateOrderItems(navigationItems, updateNavigationItem)

        // Update list for the new orders
        setNavigationMenus([...navigationMenus])
        setNavigationItems([...navigationItems])
    }, [navigationItems, navigationMenus])

    if (requestStatus === 'loading') {
        return <FullPageLoading />
    }

    if (requestStatus === 'failed' || !navigationMenus) {
        return <NotFound />
    }

    return (
        <div>
            <TopNav
                navigationMenus={navigationMenus}
                navigationItems={navigationItems}
                onDraggedMenuItem={handleDraggedMenuItem}
                onDeletedMenuItem={fetchData}
                onAddedMenuItem={fetchData}
            />
        </div>
    )
}

export default NavigationEditor
